import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { apiAuthStatus, apiLogin, apiLogout } from '../studioApi.js'

export interface AuthUser {
  label?: string
  [key: string]: unknown
}

export interface AuthStateSnapshot {
  loading: boolean
  authEnabled: boolean
  authenticated: boolean
  user: AuthUser | null
}

export interface AuthStatusResponse {
  authEnabled?: boolean
  authenticated?: boolean
  user?: AuthUser | null
}

export interface AuthContextValue extends AuthStateSnapshot {
  error: Error | null
  login: (password: string) => Promise<AuthStatusResponse>
  logout: () => Promise<void>
  refresh: () => Promise<AuthStatusResponse>
}

const initialAuthState: AuthStateSnapshot = Object.freeze({
  loading: true,
  authEnabled: false,
  authenticated: false,
  user: null,
}) as AuthStateSnapshot

const noopAsync = async (): Promise<AuthStatusResponse> => ({})

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  error: null,
  login: noopAsync,
  logout: async () => {},
  refresh: noopAsync,
})

export interface AuthProviderProps {
  children?: ReactNode
  autoFetch?: boolean
}

export function AuthProvider({
  children,
  autoFetch = true,
}: AuthProviderProps) {
  const [state, setState] = useState<AuthStateSnapshot>(initialAuthState)
  const [error, setError] = useState<Error | null>(null)

  const refresh = useCallback(async () => {
    try {
      const status: AuthStatusResponse = await apiAuthStatus()
      setState({
        loading: false,
        authEnabled: !!status.authEnabled,
        authenticated: !!status.authenticated,
        user: status.user || null,
      })
      setError(null)
      return status
    } catch (err) {
      setState((prev) => ({ ...prev, loading: false }))
      setError(err as Error)
      throw err
    }
  }, [])

  const login = useCallback(async (password: string) => {
    const status: AuthStatusResponse = await apiLogin(password)
    setState({
      loading: false,
      authEnabled: !!status.authEnabled,
      authenticated: !!status.authenticated,
      user: status.user || null,
    })
    setError(null)
    return status
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch {
      // best-effort
    }
    setState((prev) => ({
      ...prev,
      authenticated: false,
      user: null,
    }))
  }, [])

  useEffect(() => {
    if (!autoFetch) return undefined
    let cancelled = false
    refresh().catch(() => {
      // surfaced via context.error
    })
    return () => {
      cancelled = true
      void cancelled
    }
  }, [autoFetch, refresh])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      error,
      login,
      logout,
      refresh,
    }),
    [state, error, login, logout, refresh]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}

export { AuthContext }
